/**
 * Research Management - Super Admin Only
 *
 * This interface allows PageMind super admins to:
 * 1. Trigger new research for any sector/category
 * 2. Review pending research results
 * 3. Approve or reject research
 * 4. View research history
 *
 * Once approved, research becomes visible to agencies in their Sector view.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Eye,
  Check,
  X,
  Clock,
  Calendar,
  FileText,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  HelpCircle,
  AlertCircle,
  RefreshCw,
  Play,
  TrendingUp,
  Trash2,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useToast } from '@/components/ui/use-toast';
import { API_URL } from '@/config/api';

interface PerceivedAttribute {
  id: string;
  benefit_layer?: string;
  attribute_type: string;
  attribute_name: string;
  customer_description: string;
  common_phrases: string[];
  technical_features: string[];
  voice_samples?: VoiceSample[];
  research_quality_score?: number;
  purchase_driver_strength?: number;
}

interface VoiceSample {
  id: string;
  phrase: string;
  sentiment: 'positive' | 'neutral' | 'negative' | 'question';
  confidence_score: number;
}

interface ResearchSession {
  id: string;
  sector_id: number;
  sector_name: string;
  category_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  is_approved: boolean;
  approval_status: 'pending' | 'approved' | 'rejected' | null;
  attributes_count: number;
  created_at: string;
  completed_at: string;
  reviewed_by?: string;
  review_notes?: string;
}

interface Sector {
  id: number;
  name: string;
  description: string;
}

export default function ResearchManagement() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [pendingResearch, setPendingResearch] = useState<ResearchSession[]>([]);
  const [approvedResearch, setApprovedResearch] = useState<ResearchSession[]>([]);
  const [rejectedResearch, setRejectedResearch] = useState<ResearchSession[]>([]);
  const [activeTab, setActiveTab] = useState<string>('trigger');

  // Trigger Research State
  const [selectedSectorId, setSelectedSectorId] = useState<string>('');
  const [categoryName, setCategoryName] = useState<string>('');
  const [isResearching, setIsResearching] = useState(false);

  // Review Modal State
  const [reviewModal, setReviewModal] = useState<{
    open: boolean;
    session: ResearchSession | null;
    attributes: PerceivedAttribute[];
    loading: boolean;
    notes: string;
  }>({
    open: false,
    session: null,
    attributes: [],
    loading: false,
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSectors(),
        loadResearchSessions(),
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load research management data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSectors = async () => {
    try {
      const response = await authFetch(`${API_URL}/admin/sectors`);
      if (response.ok) {
        const data = await response.json();
        setSectors(data.sectors || []);
      }
    } catch (error) {
      console.error('Error loading sectors:', error);
    }
  };

  const loadResearchSessions = async () => {
    try {
      const response = await authFetch(`${API_URL}/admin/research-sessions`);
      if (response.ok) {
        const data = await response.json();

        // Organize sessions by approval status
        const pending = data.sessions.filter((s: ResearchSession) =>
          s.status === 'completed' && s.approval_status === 'pending'
        );
        const approved = data.sessions.filter((s: ResearchSession) =>
          s.approval_status === 'approved'
        );
        const rejected = data.sessions.filter((s: ResearchSession) =>
          s.approval_status === 'rejected'
        );

        setPendingResearch(pending);
        setApprovedResearch(approved);
        setRejectedResearch(rejected);
      }
    } catch (error) {
      console.error('Error loading research sessions:', error);
    }
  };

  const handleTriggerResearch = async () => {
    if (!selectedSectorId || !categoryName.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please select a sector and enter a category name',
        variant: 'destructive',
      });
      return;
    }

    setIsResearching(true);
    try {
      const response = await authFetch(`${API_URL}/category-research/${selectedSectorId}/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryName }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Research Started',
          description: `Generated ${result.attributeCount} perceived attributes for ${categoryName}`,
        });

        // Reload sessions
        await loadResearchSessions();

        // Clear form
        setCategoryName('');

        // Switch to pending review tab
        setActiveTab('pending');
      } else {
        throw new Error('Failed to start research');
      }
    } catch (error) {
      console.error('Error triggering research:', error);
      toast({
        title: 'Error',
        description: 'Failed to start research. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsResearching(false);
    }
  };

  const handleReviewResearch = async (session: ResearchSession) => {
    setReviewModal({
      open: true,
      session,
      attributes: [],
      loading: true,
      notes: session.review_notes || '',
    });

    try {
      const response = await authFetch(
        `${API_URL}/category-research/${session.sector_id}/perceptions/${encodeURIComponent(session.category_name)}`
      );

      if (response.ok) {
        const data = await response.json();
        setReviewModal(prev => ({
          ...prev,
          attributes: data.perceptions || [],
          loading: false,
        }));
      }
    } catch (error) {
      console.error('Error loading research details:', error);
      setReviewModal(prev => ({
        ...prev,
        loading: false,
      }));
      toast({
        title: 'Error',
        description: 'Failed to load research details',
        variant: 'destructive',
      });
    }
  };

  const handleApproveResearch = async () => {
    if (!reviewModal.session) return;

    try {
      const response = await authFetch(
        `${API_URL}/admin/research-sessions/${reviewModal.session.id}/approve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notes: reviewModal.notes,
          }),
        }
      );

      if (response.ok) {
        toast({
          title: 'Research Approved',
          description: `${reviewModal.session.category_name} research is now visible to agencies`,
        });

        // Reload sessions
        await loadResearchSessions();

        // Close modal
        setReviewModal({
          open: false,
          session: null,
          attributes: [],
          loading: false,
          notes: '',
        });
      } else {
        throw new Error('Failed to approve research');
      }
    } catch (error) {
      console.error('Error approving research:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve research',
        variant: 'destructive',
      });
    }
  };

  const handleRejectResearch = async () => {
    if (!reviewModal.session || !reviewModal.notes.trim()) {
      toast({
        title: 'Rejection Reason Required',
        description: 'Please provide notes explaining why this research is rejected',
        variant: 'destructive',
      });
      return;
    }

    try {
      const response = await authFetch(
        `${API_URL}/admin/research-sessions/${reviewModal.session.id}/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            notes: reviewModal.notes,
          }),
        }
      );

      if (response.ok) {
        toast({
          title: 'Research Rejected',
          description: 'Research has been rejected and will not be visible to agencies',
        });

        // Reload sessions
        await loadResearchSessions();

        // Close modal
        setReviewModal({
          open: false,
          session: null,
          attributes: [],
          loading: false,
          notes: '',
        });
      } else {
        throw new Error('Failed to reject research');
      }
    } catch (error) {
      console.error('Error rejecting research:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject research',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteResearch = async (session: ResearchSession) => {
    if (!confirm(`Are you sure you want to delete the research for "${session.category_name}"? This will permanently delete all ${session.attributes_count} perceived attributes and cannot be undone.`)) {
      return;
    }

    try {
      const response = await authFetch(
        `${API_URL}/admin/research-sessions/${session.id}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        toast({
          title: 'Research Deleted',
          description: `Research for ${session.category_name} has been permanently deleted`,
        });

        // Reload sessions
        await loadResearchSessions();
      } else {
        throw new Error('Failed to delete research');
      }
    } catch (error) {
      console.error('Error deleting research:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete research. Please try again.',
        variant: 'destructive',
      });
    }
  };

  function getAttributeTypeBadge(type: string) {
    const colors: Record<string, string> = {
      functional: 'bg-blue-100 text-blue-800',
      use_case: 'bg-green-100 text-green-800',
      sustainability: 'bg-emerald-100 text-emerald-800',
      maintenance: 'bg-orange-100 text-orange-800',
      trust: 'bg-purple-100 text-purple-800',
      value: 'bg-yellow-100 text-yellow-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }

  function getSentimentIcon(sentiment: string) {
    switch (sentiment) {
      case 'positive':
        return <ThumbsUp className="h-3 w-3" />;
      case 'negative':
        return <ThumbsDown className="h-3 w-3" />;
      case 'question':
        return <HelpCircle className="h-3 w-3" />;
      default:
        return <MessageSquare className="h-3 w-3" />;
    }
  }

  function getSentimentBadgeClass(sentiment: string) {
    switch (sentiment) {
      case 'positive':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'negative':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'question':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  }

  const renderResearchCard = (session: ResearchSession) => (
    <Card key={session.id} className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg flex items-center gap-2">
              {session.category_name}
              {session.approval_status === 'approved' && (
                <Badge variant="default" className="bg-green-500">
                  <Check className="h-3 w-3 mr-1" />
                  Approved
                </Badge>
              )}
              {session.approval_status === 'rejected' && (
                <Badge variant="destructive">
                  <X className="h-3 w-3 mr-1" />
                  Rejected
                </Badge>
              )}
              {session.approval_status === 'pending' && (
                <Badge variant="secondary">
                  <Clock className="h-3 w-3 mr-1" />
                  Pending Review
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1">
              {session.sector_name} • {session.attributes_count} attributes
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Calendar className="h-3 w-3" />
            {new Date(session.created_at).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleReviewResearch(session)}
            >
              <Eye className="h-4 w-4 mr-2" />
              Review
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteResearch(session);
              }}
              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {session.review_notes && (
          <Alert className="mt-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Review Notes:</strong> {session.review_notes}
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Sparkles className="h-8 w-8" />
          Research Management
        </h1>
        <p className="text-muted-foreground mt-2">
          Trigger, review, and approve sector research for agency consumption
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="trigger">
            <Play className="h-4 w-4 mr-2" />
            Trigger Research
          </TabsTrigger>
          <TabsTrigger value="pending">
            <Clock className="h-4 w-4 mr-2" />
            Pending Review ({pendingResearch.length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            <Check className="h-4 w-4 mr-2" />
            Approved ({approvedResearch.length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <X className="h-4 w-4 mr-2" />
            Rejected ({rejectedResearch.length})
          </TabsTrigger>
        </TabsList>

        {/* Trigger Research Tab */}
        <TabsContent value="trigger">
          <Card>
            <CardHeader>
              <CardTitle>Trigger New Research</CardTitle>
              <CardDescription>
                Start AI-powered research for a product category in any sector
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Sector</Label>
                <Select value={selectedSectorId} onValueChange={setSelectedSectorId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a sector" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((sector) => (
                      <SelectItem key={sector.id} value={sector.id.toString()}>
                        {sector.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Category Name</Label>
                <Input
                  placeholder="e.g., Bike wheels and tires, Running shoes, Laptops"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Enter the product category you want to research
                </p>
              </div>

              <Button
                onClick={handleTriggerResearch}
                disabled={isResearching || !selectedSectorId || !categoryName.trim()}
                className="w-full"
              >
                {isResearching ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Researching...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Start Research
                  </>
                )}
              </Button>

              <Alert>
                <TrendingUp className="h-4 w-4" />
                <AlertDescription>
                  Research typically takes 30-45 seconds and generates 8-12 perceived attributes
                  with customer voice samples and sentiment analysis.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Review Tab */}
        <TabsContent value="pending">
          <div className="space-y-4">
            {pendingResearch.length > 0 ? (
              pendingResearch.map(renderResearchCard)
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No research pending review. All research has been reviewed.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>

        {/* Approved Tab */}
        <TabsContent value="approved">
          <div className="space-y-4">
            {approvedResearch.length > 0 ? (
              approvedResearch.map(renderResearchCard)
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No approved research yet. Review pending research to make it available to agencies.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>

        {/* Rejected Tab */}
        <TabsContent value="rejected">
          <div className="space-y-4">
            {rejectedResearch.length > 0 ? (
              rejectedResearch.map(renderResearchCard)
            ) : (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No rejected research.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Review Modal */}
      <Dialog open={reviewModal.open} onOpenChange={(open) => !open && setReviewModal({ ...reviewModal, open: false })}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Review: {reviewModal.session?.category_name}
              {reviewModal.session?.approval_status === 'pending' && (
                <Badge variant="secondary">Pending</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {reviewModal.session?.sector_name} • {reviewModal.attributes.length} attributes
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[60vh]">
            {reviewModal.loading ? (
              <div className="space-y-4">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            ) : (
              <div className="space-y-6 pr-4">
                {/* Group attributes by benefit layer */}
                {['functional', 'emotional', 'aspirational'].map((layer) => {
                  const layerAttrs = reviewModal.attributes.filter(
                    (attr) => (attr.benefit_layer || 'functional') === layer
                  );

                  if (layerAttrs.length === 0) return null;

                  const layerInfo = {
                    functional: { emoji: '🧠', title: 'Functional Benefits', desc: 'Practical & Measurable', color: 'text-blue-600' },
                    emotional: { emoji: '❤️', title: 'Emotional Benefits', desc: 'Feelings & Confidence', color: 'text-red-600' },
                    aspirational: { emoji: '🌍', title: 'Aspirational Benefits', desc: 'Lifestyle & Identity', color: 'text-green-600' },
                  }[layer];

                  return (
                    <div key={layer} className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b">
                        <span className="text-2xl">{layerInfo.emoji}</span>
                        <div>
                          <h3 className={`text-lg font-semibold ${layerInfo.color}`}>
                            {layerInfo.title}
                          </h3>
                          <p className="text-xs text-muted-foreground">{layerInfo.desc}</p>
                        </div>
                        <Badge variant="outline" className="ml-auto">
                          {layerAttrs.length} attributes
                        </Badge>
                      </div>

                      {layerAttrs.map((attr) => (
                        <Card key={attr.id} className="border-l-4" style={{ borderLeftColor: layerInfo.color.replace('text-', '#') }}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-base flex items-center gap-2">
                                  {attr.attribute_name}
                                  <Badge className={getAttributeTypeBadge(attr.attribute_type)}>
                                    {attr.attribute_type.replace('_', ' ')}
                                  </Badge>
                                  {attr.purchase_driver_strength && (
                                    <Badge variant="default" className="bg-purple-100 text-purple-800">
                                      Purchase Impact: {attr.purchase_driver_strength}/10
                                    </Badge>
                                  )}
                                  {attr.research_quality_score && (
                                    <Badge variant="outline">
                                      Quality: {Math.round(attr.research_quality_score * 100)}%
                                    </Badge>
                                  )}
                                </CardTitle>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {attr.customer_description}
                                </p>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                      {/* Voice Samples */}
                      {attr.voice_samples && attr.voice_samples.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            Customer Voice Samples
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {attr.voice_samples.slice(0, 5).map((sample) => (
                              <Badge
                                key={sample.id}
                                variant="outline"
                                className={getSentimentBadgeClass(sample.sentiment)}
                              >
                                {getSentimentIcon(sample.sentiment)}
                                <span className="ml-1 text-xs">"{sample.phrase}"</span>
                                <span className="ml-1 text-[10px] opacity-70">
                                  {Math.round(sample.confidence_score * 100)}%
                                </span>
                              </Badge>
                            ))}
                            {attr.voice_samples.length > 5 && (
                              <Badge variant="outline">
                                +{attr.voice_samples.length - 5} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Common Phrases */}
                      {attr.common_phrases && attr.common_phrases.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-muted-foreground mb-2">
                            Common Customer Phrases
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {attr.common_phrases.slice(0, 8).map((phrase, idx) => (
                              <Badge key={idx} variant="secondary">
                                {phrase}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Technical Features */}
                      {attr.technical_features && attr.technical_features.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-muted-foreground mb-2">
                            Related Technical Features
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {attr.technical_features.map((feature, idx) => (
                              <Badge key={idx} variant="outline">
                                {feature}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          <div className="space-y-3">
            <div>
              <Label>Review Notes</Label>
              <Textarea
                placeholder="Add notes about this research (required for rejection)"
                value={reviewModal.notes}
                onChange={(e) => setReviewModal({ ...reviewModal, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReviewModal({ ...reviewModal, open: false })}
            >
              Close
            </Button>
            {reviewModal.session?.approval_status === 'pending' && (
              <>
                <Button
                  variant="destructive"
                  onClick={handleRejectResearch}
                  disabled={!reviewModal.notes.trim()}
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  variant="default"
                  onClick={handleApproveResearch}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Approve for Sector
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
